import type { QuestionInfo } from "./opencodeTypes"

// The answer bookkeeping for the agent's question dialog.
//
// Kept out of the component, and free of React, for the same reason
// lib/slashCommands.ts is: the part worth testing is the positional assembly of
// the reply, and it used to be four lines tangled inside the dialog where
// nothing could reach it. That is how the custom answer for one question came
// to be sent as the answer to every other one.

/**
 * What has been chosen so far, one slot per sub-question.
 *
 * All three arrays are indexed by the question's position, which is also the
 * order the agent expects the reply in. `custom` being per-index is the whole
 * point: it was a single shared string, and the answer builder fell back to it
 * for *every* unanswered question.
 */
export interface QuestionState {
  /** Chosen option labels. More than one only when the question is `multiple`. */
  selected: string[][]
  /** Free text, per question. Never shared between them. */
  custom: string[]
  /** Whether this question's answer is its free text rather than its options. */
  customOn: boolean[]
}

export function emptyState(count: number): QuestionState {
  return {
    selected: Array.from({ length: count }, () => []),
    custom: Array.from({ length: count }, () => ""),
    customOn: Array.from({ length: count }, () => false),
  }
}

/** Absent means allowed: the agent only ever sends `custom` to turn it off. */
export function allowsCustom(question: QuestionInfo): boolean {
  return question.custom !== false
}

/**
 * One question, single-select — the case that deserves no stepper chrome and
 * submits the moment an option is clicked, which is what the TUI does.
 */
export function isSingleStep(questions: QuestionInfo[]): boolean {
  return questions.length === 1 && questions[0]?.multiple !== true
}

/** The answer for one question: its free text when that is switched on, else its options. */
export function answerFor(state: QuestionState, index: number): string[] {
  if (state.customOn[index]) {
    const text = (state.custom[index] ?? "").trim()
    return text ? [text] : []
  }
  return state.selected[index] ?? []
}

export function isAnswered(state: QuestionState, index: number): boolean {
  return answerFor(state, index).length > 0
}

export function isComplete(questions: QuestionInfo[], state: QuestionState): boolean {
  return questions.every((_, index) => isAnswered(state, index))
}

/**
 * The reply body: one entry per question, in order, each an array of labels.
 *
 * Unanswered questions keep their slot as `[]` rather than being dropped —
 * the agent reads the reply positionally against its own question list, so a
 * missing entry would shift every answer after it onto the wrong question.
 */
export function buildReply(questions: QuestionInfo[], state: QuestionState): string[][] {
  return questions.map((_, index) => answerFor(state, index))
}

/** Toggle one label, respecting whether the question takes more than one. */
export function toggleOption(
  state: QuestionState,
  index: number,
  label: string,
  multiple: boolean,
): QuestionState {
  const current = state.selected[index] ?? []
  const next = multiple
    ? current.includes(label)
      ? current.filter((item) => item !== label)
      : [...current, label]
    : [label]
  return {
    ...state,
    selected: state.selected.map((item, i) => (i === index ? next : item)),
    // Picking an option is how someone leaves the free-text answer behind. The
    // text itself is kept, so switching back does not lose what they typed.
    customOn: state.customOn.map((on, i) => (i === index ? false : on)),
  }
}

export function setCustom(state: QuestionState, index: number, text: string): QuestionState {
  return { ...state, custom: state.custom.map((item, i) => (i === index ? text : item)) }
}

export function chooseCustom(state: QuestionState, index: number): QuestionState {
  return {
    ...state,
    customOn: state.customOn.map((on, i) => (i === index ? true : on)),
    // The options for this question are cleared: the answer is one thing or the
    // other, and leaving a stale selection behind is what makes a dialog send
    // an answer nobody chose.
    selected: state.selected.map((item, i) => (i === index ? [] : item)),
  }
}
